import Link from 'next/link';
import { IoArrowForward } from 'react-icons/io5';

import { Box, Button, Group, Stack, Text } from '@mantine/core';

import {
    CompanyProfile,
    CompanySubscription,
    PermissionCompany,
    PermissionTeam,
    PublishedStatus,
    SubscriptionType,
    Team,
} from 'models';
import { MarketingHelpers } from 'helpers/MarketingHelpers';
import { SubscriptionHelpers } from 'helpers/SubscriptionHelpers';
import { TeamHelpers } from 'helpers/TeamHelpers';

import { PublishButton } from 'components/company-profile/components/PublishButton';
import { useProfilePermission } from 'components/company-profile/hooks/use-profile-permission';
import { Logo } from 'components/company-profile-teaser/components/Logo';
import { useCurrentTeamCompanies } from 'hooks/use-current-team-companies';
import { useCurrentUser } from 'hooks/use-current-user';

import cx from './Dashboard.module.scss';

const DashboardPlanReplus = ({ team }: { team: Team }) => {
    const user = useCurrentUser();
    const { companies } = useCurrentTeamCompanies();
    const canManageBilling = TeamHelpers.hasUserPermission(team, user, [PermissionTeam.ADMIN, PermissionTeam.OWNER]);

    const [company] = companies;
    const canEdit = useProfilePermission(PermissionCompany.EDIT, company);

    const { subscription } = SubscriptionHelpers.getSubscriptionData(SubscriptionType.COMPANY, team.subscriptions);
    const premium = subscription === CompanySubscription.PREMIUM;

    const showUpgrade = !premium && canManageBilling;
    const showPublish = premium && canEdit && company?.status !== PublishedStatus.PUBLISHED;

    if (!company || (!showUpgrade && !showPublish)) {
        return null;
    }

    return (
        <Box className={cx.heroProfile}>
            <Group gap="sm" wrap="nowrap">
                <Logo company={company} withBorder size={36} />

                <Stack gap={6} miw={0}>
                    <Text fw={700} c="white" lh={1.2} lineClamp={1}>
                        {company.name}
                        {companies.length > 1 && (
                            <Text span c="gray.4" fw={500}>
                                {' '}
                                +{companies.length - 1}
                            </Text>
                        )}
                    </Text>
                    <Box className={cx.profileChip} data-premium={premium || undefined}>
                        {premium ? 'Premium profile' : 'Free profile'}
                    </Box>
                </Stack>
            </Group>

            {showUpgrade && (
                <Group gap="sm" wrap="nowrap" className={cx.profileUpgrade}>
                    <Text size="xs" c="gray.2" lh={1.4}>
                        See who scanned you and get boosted placement in search.
                    </Text>
                    <Button
                        component={Link}
                        href={MarketingHelpers.urls.resourcepro.pricing()}
                        variant="white"
                        color="dark"
                        size="xs"
                        rightSection={<IoArrowForward />}
                        style={{ flexShrink: 0 }}
                    >
                        Upgrade
                    </Button>
                </Group>
            )}

            {showPublish && <ProfilePublishRow company={company} />}
        </Box>
    );
};

const ProfilePublishRow = ({ company }: { company: CompanyProfile }) => {
    if (company.status === PublishedStatus.REVIEW) {
        return (
            <Text size="xs" c="gray.2" lh={1.4} className={cx.profileUpgrade}>
                Your profile is under review. We will notify you once it's published.
            </Text>
        );
    }

    return (
        <Group gap="sm" wrap="nowrap" className={cx.profileUpgrade}>
            <Text size="xs" c="gray.2" lh={1.4}>
                Your profile isn't live yet. Publish it to show up in search.
            </Text>
            <PublishButton company={company} variant="white" color="dark" size="xs" style={{ flexShrink: 0 }} />
        </Group>
    );
};

export { DashboardPlanReplus };
